import { t } from 'i18next';
import React, { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import KnowledgeThematicsModal from 'MithrilReact/KnowledgeThematicsModal/KnowledgeThematicsModal';
import Enum from 'Models/Enum';

import { useTypedSelector } from 'Libs/redux/utils';
import { getCurrentBOLanguageOrDefault } from 'Services/language/languageService';
import { translateApiErrors } from 'Services/api-error/api-error.service';
import { ContentValidationError } from 'Store/entities/content/content.types';
import { getAllThematics } from 'Store/entities/configuration/thematic/thematic.selectors';
import { translateThematicsName } from 'Store/entities/configuration/thematic/thematic.service';

import SInput from 'Components/structural/SInput/SInput';
import STextArea from 'Components/structural/STextArea/STextArea';
import STreeSelect from 'Components/structural/STreeSelect/STreeSelect';
import ULabel from 'Components/unit/ULabel/ULabel';
import UMediaPicker from 'Components/unit/UMediaPicker/UMediaPicker';
import { UToggle } from 'Components/unit/UToggle/UToggle';
import { UFieldError } from 'Components/unit/UFieldError/UFieldError';

import styles from './Panel.style';
import { CONTENT_TITLE_CHAR_LIMIT, KNOW_MORE_CHAR_LIMIT } from '../GameplaysForm/gameplay.constants';
import { BaseContentFormValues } from './contentForm.hook';

type PanelProps = {
  language: keyof typeof Enum.Languages;
};

export const Panel = ({ language }: PanelProps) => {
  const { watch, setValue, formState } = useFormContext<BaseContentFormValues>();

  const { root: errors } = formState.errors as unknown as { root: ContentValidationError };

  const userLanguage = getCurrentBOLanguageOrDefault();
  const [showKnowMore, setShowKnowMore] = useState(!!watch(`knowMore.${language}`));
  const [displayThematicModal, setDisplayThematicModal] = useState(false);
  const thematics = useTypedSelector(getAllThematics);

  const formattedThematicsForTreeSelect = useMemo(
    () =>
      translateThematicsName(userLanguage.id, thematics).map((thematic) => ({
        id: thematic.id,
        label: thematic.translatedLabel,
        parentId: thematic.ancestorId,
      })),
    [thematics, userLanguage],
  );

  const selectedThematic = useMemo(
    () => formattedThematicsForTreeSelect.find((thematic) => thematic.id === watch('thematic')),
    [watch('thematic')],
  );

  const onToggleShowKnowMore = useCallback(() => {
    if (showKnowMore) {
      setValue(`knowMore.${language}`, '');
    }
    setShowKnowMore((prev) => !prev);
  }, [showKnowMore, setShowKnowMore]);

  const nameErrors = errors?.[language]?.metadata?.name;
  const imageErrors = errors?.[language]?.metadata?.coverImage;
  const identifierErrors = errors?.[language]?.metadata?.identifier;
  const thematicErrors = errors?.[language]?.metadata?.thematic;
  const knowMoreErrors = errors?.[language]?.metadata?.knowMore;

  return (
    <div style={styles.panelContainer}>
      <h2 style={styles.title}>{t('contents:edition.content_settings')}</h2>
      <SInput
        value={watch(`name.${language}`)}
        onChange={(value) => {
          setValue(`name.${language}`, value, { shouldDirty: true });
        }}
        extraInputStyle={styles.inputBackground}
        placeholder={t('contents:edition.label_placeholder')}
        label={t('contents:edition.label')}
        required
        type="large"
        maxLength={CONTENT_TITLE_CHAR_LIMIT}
        errorMessage={translateApiErrors(nameErrors ? { name: nameErrors } : {})}
        dataTestContext="content-edit-title"
      />
      <div style={styles.firstFormContainer}>
        <div style={styles.imageContainer}>
          <ULabel required style={{ marginBottom: '8px' }}>
            {t('contents:edition.image')}
          </ULabel>
          <div>
            <UMediaPicker
              dropArea
              size="L"
              image={watch('image')?.url || ''}
              media={{
                mediaType: 'image',
                onChange: (media) => {
                  setValue('image', media, { shouldDirty: true });
                },
              }}
            />
            <UFieldError errorMessage={translateApiErrors(imageErrors ? { image: imageErrors } : {})} />
          </div>
        </div>
        <div style={styles.idSection}>
          <SInput
            extraInputStyle={styles.inputBackground}
            value={watch('identifier')}
            label={t('contents:edition.id')}
            placeholder={t('contents:edition.id_placeholder')}
            required
            type="large"
            onChange={(value) => {
              setValue('identifier', value, { shouldDirty: true });
            }}
            maxLength={5}
            style={styles.idField}
            errorMessage={translateApiErrors(identifierErrors ? { identifier: identifierErrors } : {})}
            dataTestContext="content-edit-identifier"
          />
          <div style={styles.thematicSection}>
            <ULabel required style={styles.thematicLabel}>
              {t('contents:edition.thematics')}
            </ULabel>
            <div>
              <STreeSelect
                placeholder={t('contents:edition.thematics_placeholder')}
                selectedItem={selectedThematic}
                items={formattedThematicsForTreeSelect}
                type="large"
                style={styles.thematicSelector}
                onSelect={(thematicId) => {
                  setValue('thematic', thematicId, { shouldDirty: true });
                }}
                dataTestContext="content-edit-thematic"
              />
              <UFieldError errorMessage={translateApiErrors(thematicErrors ? { thematic: thematicErrors } : {})} />
            </div>
          </div>
        </div>
      </div>
      <UToggle
        toggled={showKnowMore}
        onChange={onToggleShowKnowMore}
        outsideLabel={showKnowMore ? t('contents:edition.hide_know_more') : t('contents:edition.display_know_more')}
        outsideLabelTooltip={{
          tooltipContent: t('contents:edition.know_more_tooltip'),
          tooltipPosition: 'top',
        }}
        outsideLabelSize="XS"
      />

      {showKnowMore && (
        <STextArea
          textAreaStyle={styles.inputBackground}
          maxLength={KNOW_MORE_CHAR_LIMIT}
          value={watch(`knowMore.${language}`)}
          onChange={(value) => {
            setValue(`knowMore.${language}`, value, { shouldDirty: true });
          }}
          style={styles.knowMoreTextArea}
          label={t('contents:edition.know_more')}
          placeholder={t('contents:edition.know_more_placeholder')}
          errorMessage={translateApiErrors(knowMoreErrors ? { knowMore: knowMoreErrors } : {})}
          dataTestContext="content-edit-know-more"
        />
      )}
      <KnowledgeThematicsModal
        visible={displayThematicModal}
        onClose={() => {
          setDisplayThematicModal(false);
        }}
        onConfirm={() => {}}
      />
    </div>
  );
};
